'use client';
import React, { useEffect, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { CalendarManager, Economics } from '@benzinga/calendar-manager';
import { DateTime } from 'luxon';
import { ModuleWrapper } from './ModuleWrapper';
import { ModuleTable } from './ModuleTable';
import { TextCell, TimeCell, EstimateCell } from './TableCells';
import CountryFlag from './CountryFlag';
import type { ColumnsType } from 'antd/es/table';

interface EconomicDataTableProps {
  title?: string;
  maxRows?: number;
}

const EconomicDataTable: React.FC<EconomicDataTableProps> = ({
  title = 'Economic Data/Fed Speakers',
  maxRows = 10,
}) => {
  const session = React.useContext(SessionContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Economics[]>([]);

  useEffect(() => {
    const calendarManager = session.getManager(CalendarManager);
    calendarManager
      .getCalendarData(
        'economics',
        {
          date: DateTime.now().toFormat('yyyy-LL-dd'),
          pageSize: 100,
        },
        true,
      )
      .then(response => {
        const sortedData = (response?.ok ?? []).sort((a, b) => b.importance - a.importance).slice(0, maxRows);
        setData(sortedData);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [session, maxRows]);

  const columns: ColumnsType<Economics> = [
    {
      key: 'event_name',
      title: 'Economic Event',
      dataIndex: 'event_name',
      render: (value, record) => (
        <div className="flex items-center">
          {record?.country && <CountryFlag code={record.country} />}
          <TextCell className="font-semibold">{value}</TextCell>
        </div>
      ),
    },
    {
      key: 'time',
      title: 'Time',
      dataIndex: 'time',
      render: value => <TimeCell time={value} />,
    },
    {
      key: 'consensus',
      title: 'Estimates',
      dataIndex: 'consensus',
      render: value => <EstimateCell value={value} />,
    },
  ];

  return (
    <>
    <ModuleWrapper title={title}>
      <ModuleTable<Economics>
        columns={columns}
        data={data?.map((item, index) => ({ ...item, key: `${item.id}-${index}` })) ?? []}
        loading={loading}
        emptyText="No economic data available"
      />
    </ModuleWrapper>
  </>
  );
};  

export default EconomicDataTable;
